<script setup lang="ts">
import { onBeforeMount, ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { toastApiResult } from "@/toaster";
import useDialog from "@/ui/composables/useDialog";
import { useBaseStore } from "@/stores/base";

const router = useRouter();
const route = useRoute();
import NewColorPaletteDialog from "@/ui/components/colorPalette/NewColorPaletteDialog.vue";
import ColorPaletteColorDO = App.Data.ColorPaletteColorDO;

const chunkColors = (
	colors: ColorPaletteColorDO[]
): ColorPaletteColorDO[][] => {
	const rowSize = colors.length / 6;
	return Array.from({ length: 6 }, (_, i) =>
		colors.slice(i * rowSize, (i + 1) * rowSize)
	);
};
import { useApiClient } from "@/api/client";
import ModuleLoading from "@/ui/components/loading/moduleLoading.vue";
import ColorPaletteDO = App.Data.ColorPaletteDO;

type PaletteScope = "global" | "personal" | "all";

const props = withDefaults(defineProps<{ scope?: PaletteScope }>(), {
	scope: "all",
});

const allColorPalettes = ref<ColorPaletteDO[]>([]);

const colorPalettes = computed(() => {
	const currentUserId = useBaseStore().currentUser?.id;
	if (props.scope === "global")
		return allColorPalettes.value.filter((p) => p.user_id === null);
	if (props.scope === "personal")
		return allColorPalettes.value.filter((p) => p.user_id === currentUserId);
	return allColorPalettes.value;
});

const showOwnerColumn = computed(() => props.scope === "all");

const api = computed(() =>
	props.scope === "personal"
		? useApiClient("profile")
		: useApiClient("environment")
);

const loadColorPalettes = async () => {
	const response = await api.value.getColorPalettes();
	if (response.isOk()) {
		allColorPalettes.value = response.value;
	}
};

onBeforeMount(() => {
	loadColorPalettes();
});

const showNewPaletteDialog = ref(false);

const deleteColorPalette = async (palette: ColorPaletteDO) => {
	const confirmed = await useDialog().confirm({
		title: "Delete Color Palette",
		message: `Are you sure you want to delete "${palette.name}"?`,
		isDelete: true,
	});
	if (!confirmed) return;

	const response = await toastApiResult(
		api.value.deleteColorPalette(palette.uuid)
	);
	if (response.isOk()) {
		loadColorPalettes();
	}
};

const allHeaders = [
	{ title: "Name", align: "start" as const, key: "name", width: "350px" },
	{ title: "Default", align: "start" as const, key: "is_default" },
	{ title: "Owner", align: "start" as const, key: "user_id" },
	{
		title: "Colors",
		align: "start" as const,
		key: "colors",
		sortable: false,
	},
	{
		title: "Actions",
		align: "end" as const,
		key: "actions",
		sortable: false,
	},
];

const headers = computed(() =>
	showOwnerColumn.value
		? allHeaders
		: allHeaders.filter((h) => h.key !== "user_id")
);
</script>

<template>
	<new-color-palette-dialog
		v-if="showNewPaletteDialog"
		v-model="showNewPaletteDialog"
		:scope="scope"
		@created="loadColorPalettes"
	/>

	<module-loading>
		<v-card class="pa-5 mt-5" elevation="1">
			<div class="d-flex justify-end mb-4">
				<v-btn
					color="primary"
					variant="flat"
					@click="showNewPaletteDialog = true"
				>
					<i class="fa-duotone fa-plus fa-lg mr-3" />
					Add Color Palette
				</v-btn>
			</div>
			<v-data-table :headers="headers" :items="colorPalettes">
				<template v-slot:item.is_default="{ item }">
					<v-icon v-if="item.is_default" color="primary">mdi-check</v-icon>
					<v-icon v-else color="grey">mdi-minus</v-icon>
				</template>

				<template v-slot:item.user_id="{ item }">
					<v-chip
						v-if="item.user_id === null"
						size="small"
						color="primary"
						variant="tonal"
					>
						Global
					</v-chip>
					<v-chip v-else size="small" variant="tonal"> Personal </v-chip>
				</template>

				<template v-slot:item.colors="{ item }">
					<div class="color-grid">
						<div
							v-for="(row, rowIndex) in chunkColors(item.colors)"
							:key="rowIndex"
							class="color-row"
						>
							<div
								v-for="color in row"
								:key="color.uuid"
								class="color-swatch"
								:class="{ 'color-swatch--empty': color.hex_value === null }"
								:style="
									color.hex_value ? { backgroundColor: color.hex_value } : {}
								"
								:title="color.hex_value ?? ''"
							/>
						</div>
					</div>
				</template>

				<template v-slot:item.actions="{ item }">
					<div class="d-flex w-100 ga-2">
						<v-spacer />
						<v-btn
							size="30"
							icon
							color="primary"
							variant="text"
							:disabled="item.is_default"
							@click="router.push(`${route.path}/${item.uuid}`)"
						>
							<v-icon size="18">fa: fa-light fa-pencil</v-icon>
						</v-btn>
						<v-btn
							size="30"
							icon
							color="error"
							variant="text"
							:disabled="item.is_default"
							@click="deleteColorPalette(item)"
						>
							<v-icon size="18">fa: fa-light fa-trash-can</v-icon>
						</v-btn>
					</div>
				</template>
			</v-data-table>
		</v-card>
	</module-loading>
</template>

<style scoped lang="scss">
.color-grid {
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 6px 0;
	align-items: flex-end;
	width: 650px;
	max-width: 650px;
}

.color-row {
	width: 650px;
	max-width: 650px;
	display: flex;
	gap: 2px;
}

.color-swatch {
	width: 12px;
	height: 8px;
	border-radius: 2px;

	&--empty {
		background-color: #eeeeee;
		border: 1px solid #d5d5d5;
	}
}
</style>
